import React, { useState, useEffect } from 'react';
import { Calculator, Users, Clock } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const AverageCustomerLifespanCalculator = () => {
  const [churnRate, setChurnRate] = useState(5);
  const [averageLifespan, setAverageLifespan] = useState(0);

  useEffect(() => {
    calculateAverageLifespan();
  }, [churnRate]);

  const calculateAverageLifespan = () => {
    const lifespan = churnRate > 0 ? 1 / (churnRate / 100) : 0;
    setAverageLifespan(parseFloat(lifespan.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/average-customer-lifespan-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Average Customer Lifespan Calculator: Measure Customer Duration | Free Tool</title>
        <meta name="description" content="Calculate your Average Customer Lifespan instantly. Understand how long customers stay with your business and get expert tips to extend customer relationships." />
        <meta name="keywords" content="average customer lifespan calculator, customer duration calculator, customer relationship length, churn rate calculator, customer retention tools" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Average Customer Lifespan Calculator: Extend Customer Relationships" />
        <meta property="og:description" content="Calculate your Average Customer Lifespan instantly. Get insights and strategies to extend customer relationships and reduce churn." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Average Customer Lifespan Calculator: Extend Customer Relationships" />
        <meta property="twitter:description" content="Calculate your Average Customer Lifespan instantly. Get insights and strategies to extend customer relationships and reduce churn." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Average Customer Lifespan Calculator",
            "description": "Free calculator to measure and optimize average customer lifespan",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Average Customer Lifespan",
              "Measure Customer Duration",
              "Track Churn Impact",
              "Compare with Industry Benchmarks",
              "Get Optimization Tips"
            ],
            "about": {
              "@type": "Thing",
              "name": "Average Customer Lifespan",
              "description": "The average duration of a customer's relationship with a business, from first purchase to final interaction."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Business Owners, Customer Success Managers, Marketing Professionals"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Average Customer Lifespan",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter your customer churn rate percentage"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your calculated average customer lifespan"
                },
                {
                  "@type": "HowToStep",
                  "text": "Compare with industry benchmarks"
                },
                {
                  "@type": "HowToStep",
                  "text": "Review optimization strategies"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Average Customer Lifespan Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Churn Rate (%)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={churnRate}
                  onChange={(e) => setChurnRate(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {averageLifespan}
            <span className="text-xl text-gray-500 ml-2">years</span>
            <Clock className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Average Customer Lifespan</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          The Average Customer Lifespan is {averageLifespan} years. This means that, on average, a customer stays with your business for {averageLifespan} years before churning. A longer average lifespan indicates better customer retention and loyalty.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>SaaS B2B: 3-7 years</li>
          <li>E-commerce: 2-3 years</li>
          <li>Retail: 3-5 years</li>
          <li>Financial Services: 5-10 years</li>
          <li>Insurance: 7-12 years</li>
          <li>Telecommunications: 4-6 years</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Increase Average Customer Lifespan</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Focus on customer satisfaction and experience</li>
          <li>Implement a strong onboarding process for new customers</li>
          <li>Regularly gather and act on customer feedback</li>
          <li>Offer loyalty programs or rewards for long-term customers</li>
          <li>Provide excellent customer support and service</li>
          <li>Continuously improve and update your product or service</li>
          <li>Develop personalized communication strategies</li>
          <li>Create customer success programs</li>
          <li>Monitor and address early warning signs of churn</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Business Impact</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Longer customer lifespans lead to higher Customer Lifetime Value (CLV)</li>
          <li>Reduced acquisition costs through customer retention</li>
          <li>Increased word-of-mouth marketing from loyal customers</li>
          <li>More stable revenue predictions</li>
          <li>Higher probability of cross-selling and upselling</li>
        </ul>
      </div>
    </div>
  );
};

export default AverageCustomerLifespanCalculator;
